import { useCallback, useEffect, useRef, useState } from 'react';
import { toChatErrorCode } from '../client/errorCode';
import type { ChatApi } from '../client/client';
import type { ChatErrorCode, ChatMessage } from '../client/types';

/** Consecutive manual reloads offered after the initial load fails. Mirrors `useChat`'s `MAX_RETRIES`. */
const MAX_RELOADS = 2;

export type UseChatHistoryResult = {
    messages: ChatMessage[];
    /** The conversation's message cap, from the session. Null until the load resolves. */
    maxMessages: number | null;
    /** Present once the session is complete. Undefined while loading or in progress. */
    completedAt: string | undefined;
    loading: boolean;
    /** Kept for diagnostics only — consumers must render copy derived from `errorCode`, never this. */
    error: Error | null;
    /** The contract error code behind a load failure, classified via the shared `toChatErrorCode`. */
    errorCode: ChatErrorCode | null;
    /**
     * Re-runs the load. A no-op once any load has succeeded (see the hook's doc comment)
     * and a no-op once `reloadBudgetExhausted` is true.
     */
    reload: () => void;
    /** True once `reload` has been used up; callers should escalate to "refresh the page". */
    reloadBudgetExhausted: boolean;
};

/**
 * Loads the conversation history, then allows a bounded manual reload while the load
 * has never succeeded.
 *
 * Fetch-once-per-success is the whole design, not a simplification: from the moment
 * this resolves successfully, `useChat` owns the conversation and appends to it. A
 * second fetch after a success would return messages already appended locally and
 * duplicate them. A manual `reload()` is safe only because it is permitted *only*
 * while no load has ever succeeded — it recovers a failed initial load, it never
 * revalidates a good one, and it is capped at `MAX_RELOADS` attempts (exposed as
 * `reloadBudgetExhausted` so a caller stops offering it once spent, typically
 * escalating to "refresh the page"). When the client and server diverge *after* a
 * successful load, the server tells us via `conversation_stale`, and the recovery is a
 * full page reload — which keeps this hook's own reload start-up only.
 */
export function useChatHistory(briefId: string, api: ChatApi): UseChatHistoryResult {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [maxMessages, setMaxMessages] = useState<number | null>(null);
    const [completedAt, setCompletedAt] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [errorCode, setErrorCode] = useState<ChatErrorCode | null>(null);
    const [reloadCount, setReloadCount] = useState(0);
    const [reloadAttempt, setReloadAttempt] = useState(0);
    // A ref, not state: `reload` reads it synchronously to decide whether to no-op, and
    // it must reflect a success recorded moments earlier in the same effect run without
    // waiting for a re-render.
    const hasSucceededRef = useRef(false);
    const reloadStateBriefIdRef = useRef(briefId);

    useEffect(() => {
        // Routing swaps `briefId` without remounting, so the previous brief's success and
        // spent reload budget would otherwise carry over and permanently disable `reload`.
        if (reloadStateBriefIdRef.current !== briefId) {
            reloadStateBriefIdRef.current = briefId;
            hasSucceededRef.current = false;
            setReloadCount(0);
        }

        // Guards against a resolved request writing to state after the brief changed
        // (or the component unmounted), which would show one brief's history under
        // another's id.
        let active = true;

        setLoading(true);
        setError(null);
        setErrorCode(null);

        void api
            .GET('/briefs/{brief_id}/chat-session', {
                params: { path: { brief_id: briefId } },
            })
            .then(({ data, error: responseError }) => {
                if (!active) return;

                if (responseError !== undefined || data === undefined) {
                    setError(toError(responseError));
                    setErrorCode(toChatErrorCode(responseError));
                    setMessages([]);
                    setMaxMessages(null);
                    return;
                }

                hasSucceededRef.current = true;
                setMessages(data.messages);
                setMaxMessages(data.maxMessages);
                setCompletedAt(data.completedAt);
            })
            .catch((cause: unknown) => {
                if (active) {
                    setError(toError(cause));
                    setErrorCode(toChatErrorCode(cause));
                }
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, briefId, reloadAttempt]);

    const reloadBudgetExhausted = reloadCount >= MAX_RELOADS;

    const reload = useCallback(() => {
        if (hasSucceededRef.current) return;
        if (reloadCount >= MAX_RELOADS) return;

        setReloadCount((count) => count + 1);
        setReloadAttempt((attempt) => attempt + 1);
    }, [reloadCount]);

    return {
        messages,
        maxMessages,
        completedAt,
        loading,
        error,
        errorCode,
        reload,
        reloadBudgetExhausted,
    };
}

function toError(error: unknown): Error {
    if (error instanceof Error) return error;
    if (
        typeof error === 'object' &&
        error !== null &&
        typeof (error as { message?: unknown }).message === 'string'
    ) {
        return new Error((error as { message: string }).message);
    }
    if (typeof error === 'string' && error.length > 0) return new Error(error);
    return new Error('Failed to load chat history');
}
